/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { getSchriftarten, createSchriftart, updateSchriftart, deleteSchriftart, uploadSchriftartFont } from '../services/api';
import './SchriftartenVerwaltung.css';

function SchriftartenVerwaltung() {
  const [schriftarten, setSchriftarten] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [formData, setFormData] = useState({
    name: '',
    fallbacks: ''
  });
  const [uploadData, setUploadData] = useState({
    name: '',
    fallbacks: '',
    weight: '400',
    style: 'normal',
    file: null
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadSchriftarten();
  }, []);

  const loadSchriftarten = async () => {
    try {
      setLoading(true);
      const data = await getSchriftarten();
      setSchriftarten(data);
    } catch (error) {
      showMessage('error', 'Fehler beim Laden der Schriftarten');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUploadChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'file') {
      setUploadData(prev => ({ ...prev, file: files && files[0] ? files[0] : null }));
    } else {
      setUploadData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = {
      name: formData.name,
      fallbacks: formData.fallbacks
        .split(',')
        .map(f => f.trim())
        .filter(Boolean)
    };

    try {
      if (editingId) {
        await updateSchriftart(editingId, data);
        showMessage('success', 'Schriftart erfolgreich aktualisiert');
      } else {
        await createSchriftart(data);
        showMessage('success', 'Schriftart erfolgreich erstellt');
      }

      resetForm();
      loadSchriftarten();
    } catch (error) {
      showMessage('error', 'Fehler beim Speichern der Schriftart');
    }
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!uploadData.file) {
      showMessage('error', 'Bitte eine Schriftdatei auswählen (.woff/.woff2)');
      return;
    }

    const form = new FormData();
    form.append('name', uploadData.name);
    form.append('fallbacks', uploadData.fallbacks);
    form.append('weight', uploadData.weight);
    form.append('style', uploadData.style);
    form.append('fontFile', uploadData.file);
    try {
      await uploadSchriftartFont(form);
      showMessage('success', 'Schriftdatei hochgeladen und registriert');
      setUploadData({
        name: '',
        fallbacks: '',
        weight: '400',
        style: 'normal',
        file: null
      });
      // reset file input value
      const fileInput = document.getElementById('font-file-upload');
      if (fileInput) fileInput.value = '';
      loadSchriftarten();
    } catch (error) {
      showMessage('error', 'Fehler beim Hochladen der Schriftdatei');
    }
  };

  const handleEdit = (schriftart) => {
    setFormData({
      name: schriftart.name,
      fallbacks: (schriftart.fallbacks || []).join(', ')
    });
    setEditingId(schriftart.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Möchten Sie diese Schriftart wirklich löschen?')) {
      try {
        await deleteSchriftart(id);
        showMessage('success', 'Schriftart erfolgreich gelöscht');
        loadSchriftarten();
      } catch (error) {
        showMessage('error', 'Fehler beim Löschen der Schriftart');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      fallbacks: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) {
    return <div className="loading">Lade Schriftarten...</div>;
  }

  return (
    <div className="schriftarten-verwaltung">
      {message.text && (
        <div className={`alert alert-${message.type}`}>{message.text}</div>
      )}

      <div className="header">
        <h2>Schriftarten</h2>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Abbrechen' : '+ Neue Schriftart'}
        </button>
      </div>

      {showForm && (
        <div className="card schriftart-form">
          <h3>{editingId ? 'Schriftart bearbeiten' : 'Neue Schriftart'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="font-name">Schriftart-Name *</label>
              <input
                id="font-name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="z.B. Arial, Times New Roman, ..."
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="font-fallbacks">Fallbacks (optional, Komma-getrennt)</label>
              <input
                id="font-fallbacks"
                type="text"
                name="fallbacks"
                value={formData.fallbacks}
                onChange={handleInputChange}
                placeholder="z.B. Roboto, Open Sans, Arial"
              />
              <small className="hint">
                Wird Schrift x nicht gefunden, werden die Fallbacks der Reihe nach versucht.
              </small>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingId ? 'Aktualisieren' : 'Erstellen'}
              </button>
              {editingId && (
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Abbrechen
                </button>
              )}
            </div>
          </form>
        </div>
      )}

      <div className="card schriftart-form">
        <h3>Webfont hochladen</h3>
        <form onSubmit={handleUploadSubmit}>
          <div className="form-group">
            <label htmlFor="font-upload-name">Schriftart-Name *</label>
            <input
              id="font-upload-name"
              type="text"
              name="name"
              value={uploadData.name}
              onChange={handleUploadChange}
              placeholder="z.B. MeineSchrift"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="font-upload-fallbacks">Fallbacks (optional, Komma-getrennt)</label>
            <input
              id="font-upload-fallbacks"
              type="text"
              name="fallbacks"
              value={uploadData.fallbacks}
              onChange={handleUploadChange}
              placeholder="z.B. Roboto, Open Sans, Arial"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="font-upload-weight">Gewicht</label>
              <select
                id="font-upload-weight"
                name="weight"
                value={uploadData.weight}
                onChange={handleUploadChange}
              >
                {['100','200','300','400','500','600','700','800','900'].map(w => (
                  <option key={w} value={w}>{w}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="font-upload-style">Stil</label>
              <select
                id="font-upload-style"
                name="style"
                value={uploadData.style}
                onChange={handleUploadChange}
              >
                <option value="normal">Normal</option>
                <option value="italic">Kursiv</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="font-file-upload">Font-Datei (.woff/.woff2) *</label>
            <input
              id="font-file-upload"
              type="file"
              name="file"
              accept=".woff,.woff2,font/woff,font/woff2"
              onChange={handleUploadChange}
              required
            />
            <small className="hint">Datei wird lokal gespeichert und als @font-face bereitgestellt.</small>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary">Hochladen</button>
          </div>
        </form>
      </div>

      <div className="schriftarten-list">
        {schriftarten.length === 0 && !loading ? (
          <p className="no-data">Keine Schriftarten vorhanden.</p>
        ) : (
          schriftarten.map(schriftart => (
                  <div key={schriftart.id} className="schriftart-row">
                    <div className="schriftart-info schriftart-name" style={{ fontFamily: schriftart.name }}>
                      <div>{schriftart.name}</div>
                      {schriftart.fallbacks && schriftart.fallbacks.length > 0 && (
                        <small style={{ color: '#666', display: 'block', marginTop: '4px' }}>
                          Fallbacks: {schriftart.fallbacks.join(', ')}
                        </small>
                      )}
                      {schriftart.sources && schriftart.sources.length > 0 && (
                        <small style={{ color: '#666', display: 'block', marginTop: '4px' }}>
                          Quellen: {schriftart.sources.map(src => `${src.weight || 400}/${src.style || 'normal'}`).join(', ')}
                        </small>
                      )}
                    </div>
              <div className="schriftart-actions">
                <button className="btn btn-secondary btn-sm" onClick={() => handleEdit(schriftart)}>
                  Bearbeiten
                </button>
                <button className="btn btn-danger btn-sm" onClick={() => handleDelete(schriftart.id)}>
                  Löschen
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default SchriftartenVerwaltung;
